import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Fuel,
  Plus,
  Upload,
  Download,
  Trash2,
  Pencil,
  CreditCard,
  Settings as SettingsIcon,
  AlertCircle,
  X,
  Loader2,
  Check,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  Cell,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  fuelApi,
  devicesApi,
  type FuelTransaction,
  type FuelCard,
  type FuelProfile,
  type FuelProvider,
  type FuelImportConfig,
} from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';

type Tab = 'transactions' | 'cards' | 'profiles' | 'statistics';

type FuelField =
  | 'transactionDate'
  | 'transactionTime'
  | 'vehiclePlate'
  | 'cardNumber'
  | 'fuelType'
  | 'volumeLiters'
  | 'unitPrice'
  | 'totalAmount'
  | 'taxAmount'
  | 'taxRate'
  | 'currency'
  | 'stationName'
  | 'stationCountry'
  | 'odometerKm'
  | 'externalId';

const FUEL_FIELDS: Array<{ key: FuelField; label: string; required?: boolean }> = [
  { key: 'transactionDate', label: 'Date', required: true },
  { key: 'transactionTime', label: 'Heure' },
  { key: 'totalAmount', label: 'Montant total (TTC)', required: true },
  { key: 'cardNumber', label: 'Numéro de carte' },
  { key: 'vehiclePlate', label: 'Immatriculation' },
  { key: 'fuelType', label: 'Produit / carburant' },
  { key: 'volumeLiters', label: 'Volume (litres)' },
  { key: 'unitPrice', label: 'Prix unitaire' },
  { key: 'taxAmount', label: 'Montant TVA' },
  { key: 'taxRate', label: 'Taux TVA' },
  { key: 'currency', label: 'Devise' },
  { key: 'stationName', label: 'Station' },
  { key: 'stationCountry', label: 'Pays' },
  { key: 'odometerKm', label: 'Kilométrage' },
  { key: 'externalId', label: 'N° transaction (externe)' },
];

const PROVIDERS: Array<{ key: FuelProvider; label: string }> = [
  { key: 'custom', label: 'Personnalisé' },
  { key: 'dkv', label: 'DKV Mobility' },
  { key: 'shell', label: 'Shell Card' },
  { key: 'totalenergies', label: 'TotalEnergies' },
  { key: 'as24', label: 'AS24' },
  { key: 'eurotraffic', label: 'Eurotraffic' },
];

function formatMoney(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const weekday = d.toLocaleDateString('fr-FR', { weekday: 'long' });
  const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  const rest = d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `${capitalized} ${rest}`;
}

type PeriodPreset =
  | 'all'
  | '7d'
  | '30d'
  | '90d'
  | '1y'
  | 'current_month'
  | 'previous_month'
  | 'current_year'
  | 'previous_year'
  | 'custom';

// Format Date → "YYYY-MM-DD" en tenant compte du fuseau local.
const toInputDate = (d: Date) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const PERIOD_LABELS: Record<PeriodPreset, string> = {
  all: 'toutes dates',
  '7d': '7j',
  '30d': '30j',
  '90d': '90j',
  '1y': '1 an',
  current_month: 'mois en cours',
  previous_month: 'mois précédent',
  current_year: 'année en cours',
  previous_year: 'année précédente',
  custom: 'période personnalisée',
};

export default function FuelPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [tab, setTab] = useState<Tab>('transactions');

  // Filtres partagés entre les KPI cards et le tableau Transactions, pour que
  // les chiffres affichés reflètent la sélection en cours.
  const [deviceId, setDeviceId] = useState<string>('');
  const [fuelCardId, setFuelCardId] = useState<string>('');
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('30d');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  useEffect(() => {
    if (periodPreset === 'custom') return;
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = now;
    switch (periodPreset) {
      case 'all':
        start = null;
        end = null;
        break;
      case '7d': start = new Date(now); start.setDate(start.getDate() - 7); break;
      case '30d': start = new Date(now); start.setDate(start.getDate() - 30); break;
      case '90d': start = new Date(now); start.setDate(start.getDate() - 90); break;
      case '1y': start = new Date(now); start.setFullYear(start.getFullYear() - 1); break;
      case 'current_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'previous_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'current_year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      case 'previous_year':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
    }
    setCustomStartDate(start ? toInputDate(start) : '');
    setCustomEndDate(end ? toInputDate(end) : '');
  }, [periodPreset]);

  const dateRange = {
    dateFrom: customStartDate ? new Date(`${customStartDate}T00:00:00`).toISOString() : undefined,
    dateTo: customEndDate ? new Date(`${customEndDate}T23:59:59`).toISOString() : undefined,
  };

  const { data: devicesData } = useQuery({
    queryKey: ['fuel-devices-list', tenantKey],
    queryFn: async () => (await devicesApi.list({ limit: 1000 })).data,
  });
  const devices = devicesData?.data ?? [];

  const { data: stats } = useQuery({
    queryKey: ['fuel-stats', deviceId, fuelCardId, dateRange.dateFrom, dateRange.dateTo, tenantKey],
    queryFn: async () =>
      (
        await fuelApi.getStats({
          deviceId: deviceId || undefined,
          fuelCardId: fuelCardId || undefined,
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
        })
      ).data,
  });

  const periodSuffix = PERIOD_LABELS[periodPreset] ?? '30j';

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Fuel className="w-6 h-6 text-primary-500" />
          <h1 className="text-2xl font-semibold text-gray-800">
            {t('layout.menu.fuel', 'Carburant')}
          </h1>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label={`Dépenses TTC (${periodSuffix})`} value={stats ? formatMoney(stats.totalAmount) : '—'} />
        <StatCard
          label={`Litres (${periodSuffix})`}
          value={stats ? `${stats.totalLiters.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L` : '—'}
        />
        <StatCard
          label="Prix moyen / L"
          value={stats?.avgPricePerLiter ? formatMoney(stats.avgPricePerLiter) : '—'}
        />
        <StatCard
          label="Non rapprochées"
          value={stats ? String(stats.unmatchedCount) : '—'}
          warning={(stats?.unmatchedCount ?? 0) > 0}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 flex gap-1 text-sm">
        <TabButton active={tab === 'transactions'} onClick={() => setTab('transactions')}>
          Transactions
        </TabButton>
        <TabButton active={tab === 'cards'} onClick={() => setTab('cards')}>
          <CreditCard className="w-4 h-4 inline mr-1" />
          Cartes
        </TabButton>
        <TabButton active={tab === 'profiles'} onClick={() => setTab('profiles')}>
          <SettingsIcon className="w-4 h-4 inline mr-1" />
          Profils d'import
        </TabButton>
        <TabButton active={tab === 'statistics'} onClick={() => setTab('statistics')}>
          <BarChart3 className="w-4 h-4 inline mr-1" />
          Statistiques
        </TabButton>
      </div>

      {tab === 'transactions' && (
        <TransactionsTab
          devices={devices}
          tenantKey={tenantKey}
          queryClient={queryClient}
          deviceId={deviceId}
          setDeviceId={setDeviceId}
          fuelCardId={fuelCardId}
          setFuelCardId={setFuelCardId}
          periodPreset={periodPreset}
          setPeriodPreset={setPeriodPreset}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
        />
      )}
      {tab === 'cards' && <CardsTab devices={devices} tenantKey={tenantKey} queryClient={queryClient} />}
      {tab === 'profiles' && <ProfilesTab tenantKey={tenantKey} queryClient={queryClient} />}
      {tab === 'statistics' && <StatisticsTab tenantKey={tenantKey} />}
    </div>
  );
}

// ===========================================================================
// Small UI primitives
// ===========================================================================

function StatCard({ label, value, warning }: { label: string; value: string; warning?: boolean }) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        warning
          ? 'border-amber-300 bg-amber-50'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-800 mt-1">{value}</div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 -mb-px border-b-2 transition-colors ${
        active
          ? 'border-primary-500 text-primary-600'
          : 'border-transparent text-gray-500 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  );
}

// ===========================================================================
// TRANSACTIONS TAB
// ===========================================================================

interface CommonProps {
  devices: Array<{ id: string; name: string; plate: string | null }>;
  tenantKey: string;
  queryClient: ReturnType<typeof useQueryClient>;
}

interface TransactionsTabProps extends CommonProps {
  deviceId: string;
  setDeviceId: (v: string) => void;
  fuelCardId: string;
  setFuelCardId: (v: string) => void;
  periodPreset: PeriodPreset;
  setPeriodPreset: (v: PeriodPreset) => void;
  customStartDate: string;
  setCustomStartDate: (v: string) => void;
  customEndDate: string;
  setCustomEndDate: (v: string) => void;
}

function TransactionsTab({
  devices,
  tenantKey,
  queryClient,
  deviceId,
  setDeviceId,
  fuelCardId,
  setFuelCardId,
  periodPreset,
  setPeriodPreset,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
}: TransactionsTabProps) {
  const [unmatched, setUnmatched] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<FuelTransaction | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    setCurrentPage(1);
  }, [deviceId, fuelCardId, unmatched, periodPreset, customStartDate, customEndDate]);

  const dateRange = {
    dateFrom: customStartDate ? new Date(`${customStartDate}T00:00:00`).toISOString() : undefined,
    dateTo: customEndDate ? new Date(`${customEndDate}T23:59:59`).toISOString() : undefined,
  };

  const { data: cardsData } = useQuery({
    queryKey: ['fuel-cards', tenantKey],
    queryFn: async () => (await fuelApi.listCards()).data,
  });
  const cards = cardsData?.data ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ['fuel-transactions', deviceId, fuelCardId, unmatched, periodPreset, customStartDate, customEndDate, currentPage, tenantKey],
    queryFn: async () =>
      (
        await fuelApi.listTransactions({
          deviceId: deviceId || undefined,
          fuelCardId: fuelCardId || undefined,
          unmatched: unmatched || undefined,
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
          page: currentPage,
          pageSize,
        })
      ).data,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fuelApi.deleteTransaction(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['fuel-stats'] });
      // Le module FUEL alimente aussi l'historique et les KPI de la page
      // maintenance — on rafraîchit ces caches pour rester cohérent.
      queryClient.invalidateQueries({ queryKey: ['maintenance-records'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
    },
  });

  const handleExport = async () => {
    const res = await fuelApi.exportTransactions({
      deviceId: deviceId || undefined,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    });
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fuel_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const rows = data?.data ?? [];

  // Regroupement par carte avec sous-totaux multi-devises, à la manière du
  // tableau historique de la page maintenance. La clé est l'ID de carte si
  // connue, sinon le numéro brut, sinon "—".
  const groupedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      const ka = a.fuelCard?.cardNumber ?? a.cardNumber ?? '—';
      const kb = b.fuelCard?.cardNumber ?? b.cardNumber ?? '—';
      if (ka !== kb) return ka.localeCompare(kb, 'fr');
      return new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime();
    });
    const groups: Array<{
      key: string;
      cardNumber: string;
      label: string | null;
      transactions: FuelTransaction[];
      subtotal: Record<string, number>;
      /** Total HT par devise — somme de (totalAmount − taxAmount). N'inclut
       *  que les transactions où taxAmount est renseigné. */
      subtotalHT: Record<string, number>;
      /** Devises où on a au moins une transaction avec taxAmount renseigné,
       *  pour ne pas afficher "HT" trompeur sur des saisies sans TVA. */
      hasTaxByCurrency: Record<string, boolean>;
      liters: number;
      /** Distance parcourue = odo plus récent − odo le plus ancien dans le
       *  groupe (en suivant la date de transaction, pas la valeur d'odomètre). */
      kmDriven: number | null;
    }> = [];
    for (const r of sorted) {
      const key = r.fuelCardId ?? r.cardNumber ?? '__none__';
      let group = groups[groups.length - 1];
      if (!group || group.key !== key) {
        group = {
          key,
          cardNumber: r.fuelCard?.cardNumber ?? r.cardNumber ?? 'Sans carte',
          label: r.fuelCard?.label ?? null,
          transactions: [],
          subtotal: {},
          subtotalHT: {},
          hasTaxByCurrency: {},
          liters: 0,
          kmDriven: null,
        };
        groups.push(group);
      }
      group.transactions.push(r);
      group.subtotal[r.currency] = (group.subtotal[r.currency] ?? 0) + r.totalAmount;
      if (r.taxAmount != null && r.taxAmount > 0) {
        group.subtotalHT[r.currency] =
          (group.subtotalHT[r.currency] ?? 0) + (r.totalAmount - r.taxAmount);
        group.hasTaxByCurrency[r.currency] = true;
      } else {
        // Pas de TVA enregistrée → on additionne le TTC dans HT (best effort)
        group.subtotalHT[r.currency] = (group.subtotalHT[r.currency] ?? 0) + r.totalAmount;
      }
      if (r.volumeLiters != null) group.liters += r.volumeLiters;
    }
    // Calcul de la distance par groupe une fois toutes les txs collectées.
    for (const g of groups) {
      const withOdo = g.transactions.filter((t) => t.odometerKm != null);
      if (withOdo.length < 2) continue;
      // Plus ancien (plus petite date) vs plus récent (plus grande date)
      let oldest = withOdo[0];
      let newest = withOdo[0];
      for (const t of withOdo) {
        if (new Date(t.transactionDate) < new Date(oldest.transactionDate)) oldest = t;
        if (new Date(t.transactionDate) > new Date(newest.transactionDate)) newest = t;
      }
      const delta = (newest.odometerKm ?? 0) - (oldest.odometerKm ?? 0);
      g.kmDriven = delta > 0 ? delta : null;
    }
    return groups;
  }, [rows]);

  const formatSubtotal = (sub: Record<string, number>) =>
    Object.entries(sub)
      .map(([cur, amt]) => formatMoney(amt, cur))
      .join(' + ') || '—';

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Véhicule</label>
            <select
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="">Tous les véhicules</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.plate ? ` (${d.plate})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">N° de carte</label>
            <select
              value={fuelCardId}
              onChange={(e) => setFuelCardId(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="">Toutes les cartes</option>
              {cards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.cardNumber}
                  {c.label ? ` — ${c.label}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Période</label>
            <select
              value={periodPreset}
              onChange={(e) => setPeriodPreset(e.target.value as PeriodPreset)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="all">Toutes les dates</option>
              <option value="7d">7 derniers jours</option>
              <option value="30d">30 derniers jours</option>
              <option value="90d">90 derniers jours</option>
              <option value="1y">1 an</option>
              <option value="current_month">Mois en cours</option>
              <option value="previous_month">Mois précédent</option>
              <option value="current_year">Année en cours</option>
              <option value="previous_year">Année précédente</option>
              <option value="custom">Personnalisée</option>
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Du</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => {
                  setCustomStartDate(e.target.value);
                  setPeriodPreset('custom');
                }}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Au</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => {
                  setCustomEndDate(e.target.value);
                  setPeriodPreset('custom');
                }}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="text-sm flex items-center gap-1.5 text-gray-700">
            <input
              type="checkbox"
              checked={unmatched}
              onChange={(e) => setUnmatched(e.target.checked)}
              className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            Non rapprochées uniquement
          </label>
          <div className="flex-1" />
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Exporter XLSX
          </button>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-gray-700 hover:bg-gray-800 text-white"
          >
            <Upload className="w-4 h-4" />
            Importer
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white"
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Date</th>
              <th className="px-3 py-2 text-left font-medium">Véhicule</th>
              <th className="px-3 py-2 text-left font-medium">Carte</th>
              <th className="px-3 py-2 text-left font-medium">Libellé</th>
              <th className="px-3 py-2 text-left font-medium">Produit</th>
              <th className="px-3 py-2 text-right font-medium">Litres</th>
              <th className="px-3 py-2 text-right font-medium">Kilométrage</th>
              <th className="px-3 py-2 text-right font-medium">Total TTC</th>
              <th className="px-3 py-2 text-left font-medium">Station</th>
              <th className="px-3 py-2 text-left font-medium">Source</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={11} className="text-center py-6 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center py-6 text-gray-500">
                  Aucune transaction.
                </td>
              </tr>
            )}
            {groupedRows.map((group) => {
              const collapsed = collapsedGroups.has(group.key);
              return (
              <Fragment key={group.key}>
                <tr
                  className="bg-primary-50/50 border-t border-primary-100 cursor-pointer hover:bg-primary-100/60"
                  onClick={() => toggleGroup(group.key)}
                >
                  <td colSpan={11} className="px-3 py-1.5 text-xs font-medium text-primary-800">
                    {collapsed ? (
                      <ChevronRight className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                    )}
                    <span className="font-mono">{group.cardNumber}</span>
                    {group.label && <span className="text-primary-700/80"> — {group.label}</span>}
                    <span className="text-primary-700/60 ml-2">({group.transactions.length})</span>
                  </td>
                </tr>
                {!collapsed && group.transactions.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.transactionDate)}</td>
                    <td className="px-3 py-2">
                      {r.device ? (
                        <span>
                          {r.device.name}
                          {r.device.plate ? <span className="text-gray-500"> ({r.device.plate})</span> : null}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {r.vehiclePlate ?? '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.fuelCard?.cardNumber ?? r.cardNumber ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{r.fuelCard?.label ?? '—'}</td>
                    <td className="px-3 py-2">{r.fuelType ?? '—'}</td>
                    <td className="px-3 py-2 text-right">{r.volumeLiters?.toFixed(2) ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {r.odometerKm != null
                        ? `${r.odometerKm.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} km`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <div className="font-medium">{formatMoney(r.totalAmount, r.currency)}</div>
                      {r.taxAmount != null && r.taxAmount > 0 && (
                        <div className="text-[11px] text-gray-500">
                          HT {formatMoney(r.totalAmount - r.taxAmount, r.currency)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.stationName ?? '—'}
                      {r.stationCountry ? <span className="text-gray-400"> · {r.stationCountry}</span> : null}
                    </td>
                    <td className="px-3 py-2">
                      <SourceBadge source={r.source} />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        className="p-1 text-gray-500 hover:text-primary-600"
                        title="Éditer"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm('Supprimer cette transaction ?')) deleteMutation.mutate(r.id);
                        }}
                        className="p-1 text-gray-500 hover:text-rose-600"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td colSpan={5} className="px-3 py-2 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Sous-total {group.cardNumber}
                  </td>
                  <td className="px-3 py-2 text-right text-sm font-bold text-primary-700 whitespace-nowrap">
                    {group.liters > 0
                      ? `${group.liters.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} L`
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-sm whitespace-nowrap">
                    {group.kmDriven != null ? (
                      <div className="flex flex-col items-end">
                        <span className="font-bold text-primary-700">
                          {group.kmDriven.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} km
                        </span>
                        {group.liters > 0 && (
                          <span className="text-xs text-gray-500">
                            {((group.liters * 100) / group.kmDriven).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L/100 km
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-sm whitespace-nowrap">
                    <div className="font-bold text-emerald-700">
                      {formatSubtotal(group.subtotal)} <span className="text-xs font-normal text-gray-500">TTC</span>
                    </div>
                    {Object.values(group.hasTaxByCurrency).some(Boolean) && (
                      <div className="text-[11px] text-gray-500">
                        HT {formatSubtotal(group.subtotalHT)}
                      </div>
                    )}
                  </td>
                  <td colSpan={3} />
                </tr>
              </Fragment>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Page {currentPage} sur {totalPages} ({total} transactions)
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {importOpen && <ImportModal onClose={() => setImportOpen(false)} queryClient={queryClient} />}
      {createOpen && (
        <TransactionModal
          devices={devices}
          onClose={() => setCreateOpen(false)}
          queryClient={queryClient}
        />
      )}
      {editing && (
        <TransactionModal
          devices={devices}
          transaction={editing}
          onClose={() => setEditing(null)}
          queryClient={queryClient}
        />
      )}
    </div>
  );
}

function SourceBadge({ source }: { source: string }) {
  const map: Record<string, string> = {
    IMPORT: 'bg-primary-100 text-primary-700',
    OCR: 'bg-purple-100 text-purple-700',
    MANUAL: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded ${map[source] ?? map.MANUAL}`}>
      {source}
    </span>
  );
}

// ===========================================================================
// Transaction create/edit modal
// ===========================================================================

function TransactionModal({
  devices,
  transaction,
  onClose,
  queryClient,
}: {
  devices: Array<{ id: string; name: string; plate: string | null }>;
  transaction?: FuelTransaction;
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const isEdit = !!transaction;
  const [form, setForm] = useState({
    transactionDate: transaction
      ? transaction.transactionDate.slice(0, 16)
      : new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
    deviceId: transaction?.deviceId ?? '',
    vehiclePlate: transaction?.vehiclePlate ?? '',
    cardNumber: transaction?.cardNumber ?? '',
    fuelType: transaction?.fuelType ?? '',
    volumeLiters: transaction?.volumeLiters?.toString() ?? '',
    unitPrice: transaction?.unitPrice?.toString() ?? '',
    totalAmount: transaction?.totalAmount.toString() ?? '',
    currency: transaction?.currency ?? 'EUR',
    stationName: transaction?.stationName ?? '',
    odometerKm: transaction?.odometerKm?.toString() ?? '',
    notes: transaction?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        transactionDate: new Date(form.transactionDate).toISOString(),
        deviceId: form.deviceId || null,
        vehiclePlate: form.vehiclePlate || null,
        cardNumber: form.cardNumber || null,
        fuelType: form.fuelType || null,
        volumeLiters: form.volumeLiters ? Number(form.volumeLiters) : null,
        unitPrice: form.unitPrice ? Number(form.unitPrice) : null,
        totalAmount: Number(form.totalAmount),
        currency: form.currency || 'EUR',
        stationName: form.stationName || null,
        odometerKm: form.odometerKm ? Number(form.odometerKm) : null,
        notes: form.notes || null,
      };
      if (isEdit) {
        return fuelApi.updateTransaction(transaction.id, payload as Partial<FuelTransaction>);
      }
      return fuelApi.createTransaction({ ...payload, source: 'MANUAL' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['fuel-stats'] });
      // Le module FUEL alimente aussi l'historique et les KPI de la page
      // maintenance — on rafraîchit ces caches pour rester cohérent.
      queryClient.invalidateQueries({ queryKey: ['maintenance-records'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
      onClose();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { code?: string; error?: string } } };
      if (e.response?.data?.code === 'DUPLICATE') {
        setError('Cette transaction existe déjà (dédoublonnage).');
      } else {
        setError(e.response?.data?.error ?? 'Erreur de sauvegarde');
      }
    },
  });

  return (
    <Modal onClose={onClose} title={isEdit ? 'Modifier la transaction' : 'Nouvelle transaction'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          saveMutation.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Date / Heure" required>
            <input
              type="datetime-local"
              required
              value={form.transactionDate}
              onChange={(e) => setForm({ ...form, transactionDate: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Véhicule">
            <select
              value={form.deviceId}
              onChange={(e) => setForm({ ...form, deviceId: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="">— Auto (carte/plaque) —</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.plate ? ` (${d.plate})` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Numéro de carte">
            <input
              type="text"
              value={form.cardNumber}
              onChange={(e) => setForm({ ...form, cardNumber: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Immatriculation">
            <input
              type="text"
              value={form.vehiclePlate}
              onChange={(e) => setForm({ ...form, vehiclePlate: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Produit">
            <input
              type="text"
              placeholder="Diesel, SP95, Electric…"
              value={form.fuelType}
              onChange={(e) => setForm({ ...form, fuelType: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Litres">
            <input
              type="number"
              step="0.01"
              value={form.volumeLiters}
              onChange={(e) => setForm({ ...form, volumeLiters: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Prix / L">
            <input
              type="number"
              step="0.001"
              value={form.unitPrice}
              onChange={(e) => setForm({ ...form, unitPrice: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Total TTC" required>
            <input
              type="number"
              step="0.01"
              required
              value={form.totalAmount}
              onChange={(e) => setForm({ ...form, totalAmount: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Devise">
            <input
              type="text"
              maxLength={3}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Station">
            <input
              type="text"
              value={form.stationName}
              onChange={(e) => setForm({ ...form, stationName: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Kilométrage">
            <input
              type="number"
              step="1"
              value={form.odometerKm}
              onChange={(e) => setForm({ ...form, odometerKm: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
        </div>
        <Field label="Notes">
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          />
        </Field>
        {error && <div className="text-sm text-rose-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">
            Annuler
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ===========================================================================
// IMPORT MODAL
// ===========================================================================

function ImportModal({
  onClose,
  queryClient,
}: {
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [profileId, setProfileId] = useState<string>('');
  const [result, setResult] = useState<Awaited<ReturnType<typeof fuelApi.upload>>['data'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: profilesData } = useQuery({
    queryKey: ['fuel-profiles'],
    queryFn: async () => (await fuelApi.listProfiles()).data,
  });

  useMemo(() => {
    if (!profileId && profilesData?.activeProfileId) {
      setProfileId(profilesData.activeProfileId);
    }
  }, [profileId, profilesData?.activeProfileId]);

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file || !profileId) throw new Error('NO_FILE_OR_PROFILE');
      return (await fuelApi.upload(file, profileId)).data;
    },
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['fuel-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['fuel-stats'] });
      // Le module FUEL alimente aussi l'historique et les KPI de la page
      // maintenance — on rafraîchit ces caches pour rester cohérent.
      queryClient.invalidateQueries({ queryKey: ['maintenance-records'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-stats'] });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error ?? 'Erreur d\'import');
    },
  });

  return (
    <Modal onClose={onClose} title="Importer des transactions">
      {!result ? (
        <div className="space-y-3">
          <Field label="Profil d'import" required>
            <select
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="">— Sélectionner un profil —</option>
              {profilesData?.profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.isActive ? ' (actif)' : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Fichier" required>
            <FileDropZone
              file={file}
              onChange={setFile}
              accept=".csv,.xlsx,.xls"
              hint="CSV, XLSX ou XLS — jusqu'à 20 Mo"
            />
          </Field>
          {error && <div className="text-sm text-rose-600">{error}</div>}
          <p className="text-xs text-gray-500">
            Les transactions déjà importées (même hash de ligne) sont ignorées. Les véhicules non rapprochés
            apparaîtront dans le filtre dédié pour rapprochement manuel.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">
              Annuler
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                uploadMutation.mutate();
              }}
              disabled={!file || !profileId || uploadMutation.isPending}
              className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {uploadMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              <Upload className="w-4 h-4" />
              Importer
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <ResultRow label="Lignes lues" value={result.totalRows} />
            <ResultRow label="Créées" value={result.created} positive />
            <ResultRow label="Mises à jour" value={result.updated} positive />
            <ResultRow label="Invalides" value={result.skippedInvalid} warning={result.skippedInvalid > 0} />
            <ResultRow label="Rapprochées" value={result.matched} positive />
            <ResultRow label="Non rapprochées" value={result.unmatched} warning={result.unmatched > 0} />
          </div>
          {result.errors.length > 0 && (
            <div className="text-xs text-gray-600 max-h-32 overflow-y-auto border border-gray-200 rounded p-2 bg-gray-50">
              <div className="font-medium mb-1">{result.errors.length} erreurs (premières 20)</div>
              {result.errors.slice(0, 20).map((e) => (
                <div key={`${e.row}-${e.reason}`} className="font-mono">
                  Ligne {e.row}: {e.reason}
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white">
              Fermer
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ResultRow({
  label,
  value,
  positive,
  warning,
}: {
  label: string;
  value: number;
  positive?: boolean;
  warning?: boolean;
}) {
  return (
    <div
      className={`flex justify-between px-2 py-1 rounded ${
        positive
          ? 'bg-emerald-50 text-emerald-700'
          : warning
          ? 'bg-amber-50 text-amber-700'
          : 'bg-gray-50 text-gray-700'
      }`}
    >
      <span>{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

// ===========================================================================
// CARDS TAB
// ===========================================================================

function CardsTab({ devices, tenantKey, queryClient }: CommonProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<FuelCard | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['fuel-cards', tenantKey],
    queryFn: async () => (await fuelApi.listCards()).data,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => fuelApi.deleteCard(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fuel-cards'] }),
  });

  const cards = data?.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white"
        >
          <Plus className="w-4 h-4" />
          Nouvelle carte
        </button>
      </div>
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Numéro de carte</th>
              <th className="px-3 py-2 text-left font-medium">Libellé</th>
              <th className="px-3 py-2 text-left font-medium">Fournisseur</th>
              <th className="px-3 py-2 text-left font-medium">Véhicule associé</th>
              <th className="px-3 py-2 text-left font-medium">État</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td>
              </tr>
            )}
            {!isLoading && cards.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-6 text-gray-500">
                  Aucune carte. Ajoutez-en pour rapprocher automatiquement les transactions importées.
                </td>
              </tr>
            )}
            {cards.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono text-xs">{c.cardNumber}</td>
                <td className="px-3 py-2">{c.label ?? '—'}</td>
                <td className="px-3 py-2">{c.provider ?? '—'}</td>
                <td className="px-3 py-2">
                  {c.device ? (
                    <>
                      {c.device.name}
                      {c.device.plate && <span className="text-gray-500"> ({c.device.plate})</span>}
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${c.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                    {c.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button type="button" onClick={() => setEditing(c)} className="p-1 text-gray-500 hover:text-primary-600" title="Éditer">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Supprimer la carte ${c.cardNumber} ?`)) deleteMutation.mutate(c.id);
                    }}
                    className="p-1 text-gray-500 hover:text-rose-600"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {createOpen && (
        <CardModal devices={devices} onClose={() => setCreateOpen(false)} queryClient={queryClient} />
      )}
      {editing && (
        <CardModal devices={devices} card={editing} onClose={() => setEditing(null)} queryClient={queryClient} />
      )}
    </div>
  );
}

function CardModal({
  devices,
  card,
  onClose,
  queryClient,
}: {
  devices: Array<{ id: string; name: string; plate: string | null }>;
  card?: FuelCard;
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const isEdit = !!card;
  const [form, setForm] = useState({
    cardNumber: card?.cardNumber ?? '',
    label: card?.label ?? '',
    provider: card?.provider ?? '',
    deviceId: card?.deviceId ?? '',
    isActive: card?.isActive ?? true,
  });
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return fuelApi.updateCard(card.id, {
          cardNumber: form.cardNumber,
          label: form.label || null,
          provider: form.provider || null,
          deviceId: form.deviceId || null,
          isActive: form.isActive,
        });
      }
      return fuelApi.createCard({
        cardNumber: form.cardNumber,
        label: form.label || undefined,
        provider: form.provider || undefined,
        deviceId: form.deviceId || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-cards'] });
      onClose();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { code?: string } } };
      if (e.response?.data?.code === 'DUPLICATE') {
        setError('Ce numéro de carte est déjà utilisé.');
      } else {
        setError('Erreur de sauvegarde');
      }
    },
  });

  return (
    <Modal onClose={onClose} title={isEdit ? 'Modifier la carte' : 'Nouvelle carte'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          saveMutation.mutate();
        }}
        className="space-y-3"
      >
        <Field label="Numéro de carte" required>
          <input
            type="text"
            required
            value={form.cardNumber}
            onChange={(e) => setForm({ ...form, cardNumber: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          />
        </Field>
        <Field label="Libellé">
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          />
        </Field>
        <Field label="Fournisseur">
          <input
            type="text"
            placeholder="DKV, Shell, Total…"
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          />
        </Field>
        <Field label="Véhicule">
          <select
            value={form.deviceId}
            onChange={(e) => setForm({ ...form, deviceId: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          >
            <option value="">— Aucun (non rapprochée) —</option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
                {d.plate ? ` (${d.plate})` : ''}
              </option>
            ))}
          </select>
        </Field>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
            Carte active
          </label>
        )}
        {error && <div className="text-sm text-rose-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">
            Annuler
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ===========================================================================
// PROFILES TAB
// ===========================================================================

function ProfilesTab({
  tenantKey,
  queryClient,
}: {
  tenantKey: string;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<FuelProfile | null>(null);

  const exportProfile = (p: FuelProfile) => {
    const payload = {
      __type: 'trackfleet-fuel-profile',
      __version: 1,
      name: p.name,
      config: p.config,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = p.name.replace(/[^a-zA-Z0-9_-]+/g, '_');
    a.href = url;
    a.download = `fuel-profile-${safeName}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['fuel-profiles', tenantKey],
    queryFn: async () => (await fuelApi.listProfiles()).data,
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => fuelApi.activateProfile(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fuel-profiles'] }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => fuelApi.deleteProfile(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fuel-profiles'] }),
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { code?: string } } };
      if (e.response?.data?.code === 'CANNOT_DELETE_LAST_PROFILE') {
        alert('Impossible de supprimer le dernier profil restant.');
      }
    },
  });

  const profiles = data?.profiles ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          title="Importer un profil JSON"
        >
          <Upload className="w-4 h-4" />
          Importer un profil
        </button>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white"
        >
          <Plus className="w-4 h-4" />
          Nouveau profil
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {isLoading && <div className="col-span-2 text-center py-6 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        {!isLoading && profiles.length === 0 && (
          <div className="col-span-2 text-center py-6 text-gray-500">
            Créez un premier profil d'import (DKV, Shell, Total, ou personnalisé) pour démarrer.
          </div>
        )}
        {profiles.map((p) => (
          <div key={p.id} className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-medium text-gray-800 flex items-center gap-2">
                  {p.name}
                  {p.isActive && (
                    <span className="text-[10px] uppercase font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                      Actif
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {p.config.provider} · {p.config.fileFormat.toUpperCase()} · sép. "{p.config.separator}"
                </div>
              </div>
              <div className="flex gap-1">
                {!p.isActive && (
                  <button
                    type="button"
                    onClick={() => activateMutation.mutate(p.id)}
                    className="p-1 text-gray-500 hover:text-emerald-600"
                    title="Activer"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
                <button type="button" onClick={() => setEditing(p)} className="p-1 text-gray-500 hover:text-primary-600" title="Éditer">
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => exportProfile(p)}
                  className="p-1 text-gray-500 hover:text-primary-600"
                  title="Exporter en JSON"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Supprimer le profil "${p.name}" ?`)) deleteMutation.mutate(p.id);
                  }}
                  className="p-1 text-gray-500 hover:text-rose-600"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {Object.keys(p.config.columnMapping).length} colonnes mappées · match: {p.config.matchBy.join(', ')}
            </div>
          </div>
        ))}
      </div>

      {createOpen && <ProfileModal onClose={() => setCreateOpen(false)} queryClient={queryClient} />}
      {importOpen && <ProfileImportModal onClose={() => setImportOpen(false)} queryClient={queryClient} />}
      {editing && (
        <ProfileEditModal profile={editing} onClose={() => setEditing(null)} queryClient={queryClient} />
      )}
    </div>
  );
}

function ProfileModal({
  onClose,
  queryClient,
}: {
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => fuelApi.createProfile(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-profiles'] });
      onClose();
    },
    onError: () => setError('Erreur de création'),
  });

  return (
    <Modal onClose={onClose} title="Nouveau profil d'import">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (name.trim()) createMutation.mutate();
        }}
        className="space-y-3"
      >
        <Field label="Nom du profil" required>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="DKV principal"
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
          />
        </Field>
        <p className="text-xs text-gray-500">
          Vous pourrez ensuite appliquer un modèle (DKV, Shell, Total…) depuis l'édition.
        </p>
        {error && <div className="text-sm text-rose-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">
            Annuler
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50"
          >
            Créer
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ProfileImportModal({
  onClose,
  queryClient,
}: {
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<{ name: string; config: FuelImportConfig } | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Lecture du JSON dès qu'un fichier est posé, pour valider sa structure et
  // pré-remplir le nom du profil.
  const handleFile = (f: File | null) => {
    setFile(f);
    setParsed(null);
    setError(null);
    if (!f) return;
    f.text()
      .then((text) => {
        const json = JSON.parse(text);
        if (json.__type !== 'trackfleet-fuel-profile' || !json.name || !json.config) {
          setError('Ce fichier n\'est pas un export de profil TrackFleet.');
          return;
        }
        const required: Array<keyof FuelImportConfig> = [
          'provider',
          'fileFormat',
          'separator',
          'encoding',
          'dateFormat',
          'columnMapping',
          'matchBy',
        ];
        for (const k of required) {
          if (json.config[k] === undefined) {
            setError(`Profil invalide : champ "${String(k)}" manquant.`);
            return;
          }
        }
        setParsed({ name: json.name, config: json.config });
        setName(json.name);
      })
      .catch(() => setError('Fichier JSON illisible.'));
  };

  const importMutation = useMutation({
    mutationFn: () => {
      if (!parsed) throw new Error('NO_DATA');
      return fuelApi.createProfile(name.trim() || parsed.name, parsed.config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-profiles'] });
      onClose();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { code?: string; error?: string } } };
      if (e.response?.data?.code === 'P2002') {
        setError('Un profil avec ce nom existe déjà.');
      } else {
        setError(e.response?.data?.error ?? 'Erreur d\'import');
      }
    },
  });

  return (
    <Modal onClose={onClose} title="Importer un profil">
      <div className="space-y-3">
        <FileDropZone file={file} onChange={handleFile} accept=".json,application/json" hint="Fichier .json exporté depuis un autre tenant" />
        {parsed && (
          <>
            <Field label="Nom du profil (renommer si besoin)" required>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              />
            </Field>
            <div className="text-xs text-gray-600 rounded-md bg-gray-50 border border-gray-200 px-2 py-1.5">
              <div>
                Fournisseur : <span className="font-medium">{parsed.config.provider}</span> · Format :{' '}
                <span className="font-medium">{parsed.config.fileFormat.toUpperCase()}</span>
              </div>
              <div>
                {Object.keys(parsed.config.columnMapping).length} colonnes mappées · match :{' '}
                {parsed.config.matchBy.join(', ')}
              </div>
            </div>
          </>
        )}
        {error && <div className="text-sm text-red-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">
            Annuler
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              importMutation.mutate();
            }}
            disabled={!parsed || !name.trim() || importMutation.isPending}
            className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {importMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Importer
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ProfileEditModal({
  profile,
  onClose,
  queryClient,
}: {
  profile: FuelProfile;
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [name, setName] = useState(profile.name);
  const [config, setConfig] = useState(profile.config);
  const [error, setError] = useState<string | null>(null);
  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [detectedHeaders, setDetectedHeaders] = useState<string[] | null>(null);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);

  const applyTemplateMutation = useMutation({
    mutationFn: (provider: FuelProvider) => fuelApi.getTemplate(provider),
    onSuccess: (res) => setConfig(res.data),
  });

  const inspectMutation = useMutation({
    mutationFn: () => {
      if (!sampleFile) throw new Error('NO_FILE');
      // Détection du format réel du fichier d'analyse depuis l'extension :
      // on ne se fie pas au réglage du profil pour éviter de parser un CSV
      // comme du XLSX (et inversement).
      const name = sampleFile.name.toLowerCase();
      const fileFormat: 'csv' | 'xlsx' = name.endsWith('.xlsx') || name.endsWith('.xls') ? 'xlsx' : 'csv';
      return fuelApi.inspect(sampleFile, {
        fileFormat,
        encoding: config.encoding,
        separator: config.separator,
        hasHeader: config.hasHeader,
        startRow: config.startRow,
      });
    },
    onSuccess: (res) => {
      setDetectedHeaders(res.data.headers);
      setPreviewRows(res.data.preview);
      setError(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { details?: string; error?: string } } };
      setError(e.response?.data?.details ?? e.response?.data?.error ?? 'Impossible d\'analyser le fichier');
    },
  });

  const saveMutation = useMutation({
    mutationFn: () => fuelApi.updateProfile(profile.id, { name, config }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-profiles'] });
      onClose();
    },
    onError: () => setError('Erreur de sauvegarde'),
  });

  return (
    <Modal onClose={onClose} title={`Éditer "${profile.name}"`} wide>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          saveMutation.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Nom" required>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Appliquer un modèle">
            <select
              onChange={(e) => {
                const p = e.target.value as FuelProvider;
                if (p) applyTemplateMutation.mutate(p);
              }}
              value=""
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="">— Choisir —</option>
              {PROVIDERS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Format de fichier">
            <select
              value={config.fileFormat}
              onChange={(e) => setConfig({ ...config, fileFormat: e.target.value as 'csv' | 'xlsx' })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="csv">CSV</option>
              <option value="xlsx">XLSX</option>
            </select>
          </Field>
          <Field label="Séparateur">
            <select
              value={config.separator}
              onChange={(e) => setConfig({ ...config, separator: e.target.value as FuelProfile['config']['separator'] })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value=";">Point-virgule ;</option>
              <option value=",">Virgule ,</option>
              <option value={'\t'}>Tabulation</option>
              <option value="|">Barre |</option>
            </select>
          </Field>
          <Field label="Encodage">
            <select
              value={config.encoding}
              onChange={(e) => setConfig({ ...config, encoding: e.target.value as FuelProfile['config']['encoding'] })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="utf-8">UTF-8</option>
              <option value="utf-8-bom">UTF-8 avec BOM</option>
              <option value="iso-8859-1">ISO-8859-1 (Latin-1)</option>
            </select>
          </Field>
          <Field label="Format de date">
            <select
              value={config.dateFormat}
              onChange={(e) => setConfig({ ...config, dateFormat: e.target.value as FuelProfile['config']['dateFormat'] })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="dd/MM/yyyy">DD/MM/YYYY</option>
              <option value="dd.MM.yyyy">DD.MM.YYYY</option>
              <option value="yyyy-MM-dd">YYYY-MM-DD</option>
              <option value="MM/dd/yyyy">MM/DD/YYYY</option>
            </select>
          </Field>
          <Field label="Décimale">
            <select
              value={config.decimalSeparator}
              onChange={(e) => setConfig({ ...config, decimalSeparator: e.target.value as '.' | ',' })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value=",">Virgule (1,23)</option>
              <option value=".">Point (1.23)</option>
            </select>
          </Field>
          <Field label="Devise par défaut">
            <input
              type="text"
              maxLength={3}
              value={config.defaultCurrency}
              onChange={(e) => setConfig({ ...config, defaultCurrency: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="flex items-center gap-2 text-sm md:col-span-1">
            <input
              type="checkbox"
              checked={config.hasHeader}
              onChange={(e) => setConfig({ ...config, hasHeader: e.target.checked })}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
            Le fichier contient un en-tête
          </label>
          <Field label="Ligne de départ (données)">
            <input
              type="number"
              min={1}
              max={100}
              value={config.startRow}
              onChange={(e) => setConfig({ ...config, startRow: Math.max(1, Number(e.target.value) || 1) })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </Field>
          <Field label="Rapprochement véhicule">
            <div className="flex gap-3 text-sm pt-2">
              {(['cardNumber', 'plate'] as const).map((m) => (
                <label key={m} className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={config.matchBy.includes(m)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...config.matchBy.filter((x) => x !== m), m]
                        : config.matchBy.filter((x) => x !== m);
                      if (next.length > 0) setConfig({ ...config, matchBy: next });
                    }}
                  />
                  {m === 'cardNumber' ? 'N° carte' : 'Immat.'}
                </label>
              ))}
            </div>
          </Field>
        </div>

        <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-3 space-y-3">
          <div>
            <div className="text-sm font-medium text-gray-800">Détecter les colonnes depuis un fichier</div>
            <div className="text-xs text-gray-600">
              Importez un échantillon — les en-têtes seront proposés en liste déroulante ci-dessous.
            </div>
          </div>
          <FileDropZone
            file={sampleFile}
            onChange={(f) => {
              setSampleFile(f);
              setDetectedHeaders(null);
              setPreviewRows([]);
            }}
            accept=".csv,.xlsx,.xls"
            hint="CSV, XLSX ou XLS"
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => inspectMutation.mutate()}
              disabled={!sampleFile || inspectMutation.isPending}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {inspectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Analyser
            </button>
          </div>
          {detectedHeaders && (
            <div className="text-xs text-emerald-700">
              {detectedHeaders.length} colonnes détectées
              {previewRows.length > 0 && ` · ${previewRows.length} lignes d'aperçu`}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-medium text-gray-700">Mapping des colonnes</div>
            <div className="text-xs text-gray-500">
              {detectedHeaders
                ? 'Sélectionnez la colonne source pour chaque champ.'
                : config.hasHeader
                ? 'Saisissez le nom de la colonne ou analysez un fichier exemple ci-dessus.'
                : 'Saisissez l\'index de colonne (0 = première) ou analysez un fichier.'}
            </div>
          </div>
          <div className="rounded border border-gray-200 overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Champ TrackFleet</th>
                  <th className="px-2 py-1.5 text-left font-medium w-32">Mode</th>
                  <th className="px-2 py-1.5 text-left font-medium">Valeur</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {FUEL_FIELDS.map(({ key, label, required }) => {
                  const ref = config.columnMapping[key];
                  const mode: 'none' | 'column' | 'constant' = !ref
                    ? 'none'
                    : ref.kind === 'constant'
                    ? 'constant'
                    : 'column';
                  return (
                    <tr key={key}>
                      <td className="px-2 py-1.5">
                        <span className="text-gray-700">{label}</span>
                        {required && <span className="text-rose-500 ml-0.5">*</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={mode}
                          onChange={(e) => {
                            const next = e.target.value as 'none' | 'column' | 'constant';
                            const mapping = { ...config.columnMapping };
                            if (next === 'none') {
                              delete mapping[key];
                            } else if (next === 'column') {
                              mapping[key] = { kind: 'column', ref: ref?.kind === 'column' ? ref.ref : '' };
                            } else {
                              mapping[key] = { kind: 'constant', value: ref?.kind === 'constant' ? ref.value ?? '' : '' };
                            }
                            setConfig({ ...config, columnMapping: mapping });
                          }}
                          className="px-2 py-1 bg-white border border-gray-300 rounded-md text-xs text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        >
                          <option value="none">Ignorer</option>
                          <option value="column">Colonne</option>
                          <option value="constant">Constante</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        {mode === 'none' && <span className="text-gray-400 text-xs italic">—</span>}
                        {mode === 'column' && (
                          detectedHeaders ? (
                            (() => {
                              const currentVal =
                                ref && ref.kind === 'column' ? String(ref.ref ?? '') : '';
                              // Hide headers already taken by other fields (keep current selection).
                              const usedByOthers = new Set(
                                Object.entries(config.columnMapping)
                                  .filter(([k, v]) => k !== key && v.kind === 'column' && v.ref !== undefined)
                                  .map(([, v]) => String((v as { ref: string | number }).ref)),
                              );
                              return (
                                <select
                                  value={currentVal}
                                  onChange={(e) =>
                                    setConfig({
                                      ...config,
                                      columnMapping: {
                                        ...config.columnMapping,
                                        [key]: { kind: 'column', ref: e.target.value },
                                      },
                                    })
                                  }
                                  className="w-full px-2 py-1 bg-white border border-gray-300 rounded-md text-xs text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                                >
                                  <option value="">— Choisir une colonne —</option>
                                  {detectedHeaders.map((h) => {
                                    const taken = usedByOthers.has(h) && h !== currentVal;
                                    if (taken) return null;
                                    return (
                                      <option key={h} value={h}>
                                        {h}
                                      </option>
                                    );
                                  })}
                                </select>
                              );
                            })()
                          ) : (
                            <input
                              type="text"
                              value={ref && ref.kind === 'column' ? String(ref.ref ?? '') : ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const parsed = !config.hasHeader && /^\d+$/.test(raw) ? Number(raw) : raw;
                                setConfig({
                                  ...config,
                                  columnMapping: {
                                    ...config.columnMapping,
                                    [key]: { kind: 'column', ref: parsed },
                                  },
                                });
                              }}
                              placeholder={config.hasHeader ? 'Nom de colonne' : 'Index (0, 1, 2…)'}
                              className="w-full px-2 py-1 bg-white border border-gray-300 rounded-md text-xs text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                            />
                          )
                        )}
                        {mode === 'constant' && (
                          <input
                            type="text"
                            value={ref && ref.kind === 'constant' ? ref.value ?? '' : ''}
                            onChange={(e) =>
                              setConfig({
                                ...config,
                                columnMapping: {
                                  ...config.columnMapping,
                                  [key]: { kind: 'constant', value: e.target.value },
                                },
                              })
                            }
                            placeholder='Valeur fixe (ex: "EUR", "Diesel")'
                            className="w-full px-2 py-1 bg-white border border-gray-300 rounded-md text-xs text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="text-sm font-medium mb-1 text-gray-700">
            Correspondance produit → type carburant
          </div>
          <div className="text-xs text-gray-500 mb-2">
            Ce que votre fichier écrit (ex: "Gazole", "SP95") et la catégorie TrackFleet correspondante.
          </div>
          <FuelTypeMappingEditor
            mapping={config.fuelTypeMapping}
            onChange={(fuelTypeMapping) => setConfig({ ...config, fuelTypeMapping })}
          />
        </div>

        {error && <div className="text-sm text-rose-600">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700">
            Annuler
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="px-3 py-1.5 text-sm rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Enregistrer
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ===========================================================================
// Fuel-type mapping editor
// ===========================================================================

function FuelTypeMappingEditor({
  mapping,
  onChange,
}: {
  mapping: Record<string, 'DIESEL' | 'PETROL' | 'ELECTRIC' | 'OTHER'>;
  onChange: (next: Record<string, 'DIESEL' | 'PETROL' | 'ELECTRIC' | 'OTHER'>) => void;
}) {
  const [newLabel, setNewLabel] = useState('');
  const [newCategory, setNewCategory] = useState<'DIESEL' | 'PETROL' | 'ELECTRIC' | 'OTHER'>('DIESEL');
  const entries = Object.entries(mapping);

  return (
    <div className="rounded border border-gray-200 overflow-hidden">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Libellé fichier</th>
            <th className="px-2 py-1.5 text-left font-medium w-40">Catégorie</th>
            <th className="px-2 py-1.5 w-12"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {entries.map(([label, cat]) => (
            <tr key={label}>
              <td className="px-2 py-1.5 font-mono text-xs">{label}</td>
              <td className="px-2 py-1.5">
                <select
                  value={cat}
                  onChange={(e) => onChange({ ...mapping, [label]: e.target.value as typeof cat })}
                  className="px-2 py-1 bg-white border border-gray-300 rounded-md text-xs text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                >
                  <option value="DIESEL">Diesel</option>
                  <option value="PETROL">Essence</option>
                  <option value="ELECTRIC">Électrique</option>
                  <option value="OTHER">Autre</option>
                </select>
              </td>
              <td className="px-2 py-1.5 text-right">
                <button
                  type="button"
                  onClick={() => {
                    const { [label]: _, ...rest } = mapping;
                    onChange(rest);
                  }}
                  className="p-1 text-gray-400 hover:text-rose-600"
                  title="Supprimer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}
          <tr className="bg-gray-50/50">
            <td className="px-2 py-1.5">
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder='ex: "Sans plomb 95"'
                className="w-full px-2 py-1 bg-white border border-gray-300 rounded-md text-xs text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              />
            </td>
            <td className="px-2 py-1.5">
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value as typeof newCategory)}
                className="px-2 py-1 bg-white border border-gray-300 rounded-md text-xs text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              >
                <option value="DIESEL">Diesel</option>
                <option value="PETROL">Essence</option>
                <option value="ELECTRIC">Électrique</option>
                <option value="OTHER">Autre</option>
              </select>
            </td>
            <td className="px-2 py-1.5 text-right">
              <button
                type="button"
                onClick={() => {
                  const trimmed = newLabel.trim();
                  if (!trimmed) return;
                  onChange({ ...mapping, [trimmed]: newCategory });
                  setNewLabel('');
                }}
                className="p-1 text-gray-500 hover:text-emerald-600"
                title="Ajouter"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// Generic Modal + Field
// ===========================================================================

function Modal({
  title,
  children,
  onClose,
  wide,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-content ${wide ? 'max-w-3xl' : 'max-w-xl'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FileDropZone({
  file,
  onChange,
  accept,
  hint,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  accept: string;
  hint?: string;
}) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) onChange(dropped);
      }}
      className={`block w-full rounded-lg border-2 border-dashed cursor-pointer transition-colors px-4 py-6 text-center ${
        dragOver
          ? 'border-primary-500 bg-primary-50'
          : file
          ? 'border-primary-300 bg-primary-50/40'
          : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50/30 bg-gray-50/50'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
      {file ? (
        <div className="flex items-center justify-center gap-3">
          <FileSpreadsheet className="w-6 h-6 text-primary-600" />
          <div className="text-left">
            <div className="text-sm font-medium text-gray-800 break-all">{file.name}</div>
            <div className="text-xs text-gray-500">
              {(file.size / 1024).toFixed(1)} ko — cliquez pour changer
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onChange(null);
              if (inputRef.current) inputRef.current.value = '';
            }}
            className="ml-2 p-1 text-gray-400 hover:text-red-500"
            title="Retirer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Upload className="w-8 h-8 text-gray-400" />
          <div className="text-sm font-medium text-gray-700">
            Glissez un fichier ici ou <span className="text-primary-600 underline">cliquez pour parcourir</span>
          </div>
          {hint && <div className="text-xs text-gray-500">{hint}</div>}
        </div>
      )}
    </label>
  );
}

// ===========================================================================
// STATISTICS TAB
// ===========================================================================

type StatsPeriod = '1y' | '2y' | '3y' | 'custom';
type Granularity = 'year' | 'month';

// Palette utilisée pour colorer chaque année différemment. En vue mensuelle,
// chaque mois prend la couleur de son année — ce qui rend la frontière entre
// années visible d'un coup d'œil.
const YEAR_COLORS = [
  '#0ea5e9', // sky
  '#22c55e', // green
  '#a855f7', // purple
  '#f59e0b', // amber
  '#ef4444', // red
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

function StatisticsTab({ tenantKey }: { tenantKey: string }) {
  const [fuelCardId, setFuelCardId] = useState<string>('');
  const [period, setPeriod] = useState<StatsPeriod>('3y');
  const [granularity, setGranularity] = useState<Granularity>('year');
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');

  // Recalcule les bornes de date à chaque changement de période, sauf en mode
  // personnalisé (où l'utilisateur saisit librement). La granularité reste
  // sous le contrôle de l'utilisateur (ex: 3 ans + Mensuelle → 3 barres par mois).
  useEffect(() => {
    if (period === 'custom') return;
    const now = new Date();
    const years = period === '1y' ? 1 : period === '2y' ? 2 : 3;
    const start = new Date(now);
    start.setFullYear(start.getFullYear() - years);
    setCustomStart(toInputDate(start));
    setCustomEnd(toInputDate(now));
  }, [period]);

  const dateRange = {
    dateFrom: customStart ? new Date(`${customStart}T00:00:00`).toISOString() : undefined,
    dateTo: customEnd ? new Date(`${customEnd}T23:59:59`).toISOString() : undefined,
  };

  const { data: cardsData } = useQuery({
    queryKey: ['fuel-cards', tenantKey],
    queryFn: async () => (await fuelApi.listCards()).data,
  });
  const cards = cardsData?.data ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ['fuel-timeseries', fuelCardId, dateRange.dateFrom, dateRange.dateTo, granularity, tenantKey],
    queryFn: async () =>
      (
        await fuelApi.getTimeseries({
          fuelCardId: fuelCardId || undefined,
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
          granularity,
        })
      ).data,
  });

  const series = data?.data ?? [];

  // Totaux affichés au-dessus des graphiques pour donner du contexte chiffré.
  const totals = useMemo(() => {
    let amount = 0;
    let liters = 0;
    let count = 0;
    for (const p of series) {
      amount += p.amount;
      liters += p.liters;
      count += p.transactionCount;
    }
    return {
      amount,
      liters,
      count,
      avgPricePerLiter: liters > 0 ? amount / liters : null,
    };
  }, [series]);

  const formatPeriodLabel = (period: string): string => {
    if (granularity === 'year') return period;
    // YYYY-MM → MM/YYYY (plus lisible en FR)
    const [y, m] = period.split('-');
    return `${m}/${y}`;
  };

  // Couleur par année — déterministe pour que la même année ait toujours la
  // même couleur entre les deux graphiques.
  const colorByYear = useMemo(() => {
    const years = Array.from(new Set(series.map((p) => p.period.slice(0, 4)))).sort();
    const map = new Map<string, string>();
    years.forEach((y, i) => map.set(y, YEAR_COLORS[i % YEAR_COLORS.length]));
    return map;
  }, [series]);

  const colorForPoint = (period: string): string =>
    colorByYear.get(period.slice(0, 4)) ?? YEAR_COLORS[0];

  // Totaux par année + variation N vs N-1 (en %). Sert au tableau récap et aux
  // étiquettes au-dessus des barres en vue Annuelle.
  const yearlyTotals = useMemo(() => {
    const map = new Map<string, { amount: number; liters: number }>();
    for (const p of series) {
      const year = p.period.slice(0, 4);
      const cur = map.get(year) ?? { amount: 0, liters: 0 };
      cur.amount += p.amount;
      cur.liters += p.liters;
      map.set(year, cur);
    }
    const years = Array.from(map.keys()).sort();
    return years.map((y, i) => {
      const cur = map.get(y)!;
      const prev = i > 0 ? map.get(years[i - 1])! : null;
      const pct = (a: number, b: number): number | null =>
        b > 0 ? ((a - b) / b) * 100 : null;
      return {
        year: y,
        amount: cur.amount,
        liters: cur.liters,
        amountChangePct: prev ? pct(cur.amount, prev.amount) : null,
        litersChangePct: prev ? pct(cur.liters, prev.liters) : null,
      };
    });
  }, [series]);

  // Lookup année → variation, pour injecter le % directement sur les barres.
  const amountPctByYear = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const r of yearlyTotals) m.set(r.year, r.amountChangePct);
    return m;
  }, [yearlyTotals]);
  const litersPctByYear = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const r of yearlyTotals) m.set(r.year, r.litersChangePct);
    return m;
  }, [yearlyTotals]);

  // Enrichit la série annuelle avec le % pour pouvoir passer le LabelList
  // recharts directement sur dataKey="pctAmount" / "pctLiters".
  const seriesWithPct = useMemo(
    () =>
      series.map((p) => {
        const year = p.period.slice(0, 4);
        return {
          ...p,
          pctAmount: amountPctByYear.get(year) ?? null,
          pctLiters: litersPctByYear.get(year) ?? null,
        };
      }),
    [series, amountPctByYear, litersPctByYear],
  );

  // En vue mensuelle on pivote la série pour grouper par mois (X = 12 mois)
  // avec une barre par année côte à côte. Recharts assigne automatiquement
  // un offset aux barres partageant le même <XAxis dataKey> => groupage natif.
  const MONTH_LABELS_FR = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
  const monthlyPivot = useMemo(() => {
    if (granularity !== 'month') return null;
    const years = Array.from(colorByYear.keys());
    const rows: Array<Record<string, string | number>> = MONTH_LABELS_FR.map((label) => {
      const row: Record<string, string | number> = { month: label };
      // Init à 0 pour que chaque barre soit dessinée (sinon Recharts saute la
      // série pour les mois où une année n'a pas de point).
      for (const y of years) {
        row[`${y}_amount`] = 0;
        row[`${y}_liters`] = 0;
      }
      return row;
    });
    for (const p of series) {
      const [y, m] = p.period.split('-');
      const idx = parseInt(m, 10) - 1;
      if (idx < 0 || idx > 11) continue;
      rows[idx][`${y}_amount`] = p.amount;
      rows[idx][`${y}_liters`] = p.liters;
    }
    return { rows, years };
  }, [granularity, series, colorByYear]);

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-white border border-gray-200 p-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Carte</label>
            <select
              value={fuelCardId}
              onChange={(e) => setFuelCardId(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="">Toutes les cartes</option>
              {cards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.cardNumber}
                  {c.label ? ` — ${c.label}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Période</label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as StatsPeriod)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="1y">1 an</option>
              <option value="2y">2 ans</option>
              <option value="3y">3 ans</option>
              <option value="custom">Personnalisée</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Granularité</label>
            <select
              value={granularity}
              onChange={(e) => setGranularity(e.target.value as Granularity)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            >
              <option value="year">Annuelle</option>
              <option value="month">Mensuelle</option>
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Du</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => {
                  setCustomStart(e.target.value);
                  setPeriod('custom');
                }}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">Au</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => {
                  setCustomEnd(e.target.value);
                  setPeriod('custom');
                }}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Dépenses TTC (période)" value={formatMoney(totals.amount)} />
        <StatCard
          label="Litres (période)"
          value={`${totals.liters.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L`}
        />
        <StatCard
          label="Prix moyen / L"
          value={totals.avgPricePerLiter ? formatMoney(totals.avgPricePerLiter) : '—'}
        />
        <StatCard label="Transactions" value={String(totals.count)} />
      </div>

      {colorByYear.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-1 text-xs text-gray-600">
          <span className="text-gray-500">Années :</span>
          {Array.from(colorByYear.entries()).map(([year, color]) => (
            <span key={year} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: color }} />
              {year}
            </span>
          ))}
        </div>
      )}

      {yearlyTotals.length > 0 && (
        <div className="rounded-lg bg-white border border-gray-200 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Année</th>
                <th className="px-3 py-2 text-right font-medium">Coût TTC</th>
                <th className="px-3 py-2 text-right font-medium">vs N-1</th>
                <th className="px-3 py-2 text-right font-medium">Litres</th>
                <th className="px-3 py-2 text-right font-medium">vs N-1</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {yearlyTotals.map((r) => (
                <tr key={r.year} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: colorByYear.get(r.year) ?? YEAR_COLORS[0] }}
                    />
                    {r.year}
                  </td>
                  <td className="px-3 py-2 text-right">{formatMoney(r.amount)}</td>
                  <td className="px-3 py-2 text-right">
                    <PctDelta value={r.amountChangePct} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.liters.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L
                  </td>
                  <td className="px-3 py-2 text-right">
                    <PctDelta value={r.litersChangePct} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <ChartCard title="Évolution du coût TTC" subtitle={granularity === 'year' ? 'par année' : 'par mois'}>
          {isLoading ? (
            <ChartLoader />
          ) : series.length === 0 ? (
            <ChartEmpty />
          ) : granularity === 'month' && monthlyPivot ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthlyPivot.rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" tick={{ fill: '#4b5563', fontSize: 12 }} />
                <YAxis
                  stroke="#6b7280"
                  tick={{ fill: '#4b5563', fontSize: 12 }}
                  tickFormatter={(v) =>
                    typeof v === 'number' ? v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : v
                  }
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [formatMoney(Number(value ?? 0)), String(name).replace('_amount', '')]}
                />
                {monthlyPivot.years.map((y) => (
                  <Bar
                    key={`cost-${y}`}
                    dataKey={`${y}_amount`}
                    name={`${y}_amount`}
                    fill={colorByYear.get(y) ?? YEAR_COLORS[0]}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={seriesWithPct} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="period"
                  stroke="#6b7280"
                  tick={{ fill: '#4b5563', fontSize: 12 }}
                  tickFormatter={formatPeriodLabel}
                />
                <YAxis
                  stroke="#6b7280"
                  tick={{ fill: '#4b5563', fontSize: 12 }}
                  tickFormatter={(v) =>
                    typeof v === 'number' ? v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : v
                  }
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                  labelFormatter={(label) => formatPeriodLabel(String(label))}
                  formatter={(value) => [formatMoney(Number(value ?? 0)), 'Coût TTC']}
                />
                <Bar dataKey="amount" name="Coût TTC (€)" radius={[4, 4, 0, 0]}>
                  {seriesWithPct.map((entry) => (
                    <Cell key={`cost-${entry.period}`} fill={colorForPoint(entry.period)} />
                  ))}
                  <LabelList dataKey="pctAmount" content={renderPctLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard
          title="Évolution de la consommation"
          subtitle={granularity === 'year' ? 'par année' : 'par mois'}
        >
          {isLoading ? (
            <ChartLoader />
          ) : series.length === 0 ? (
            <ChartEmpty />
          ) : granularity === 'month' && monthlyPivot ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthlyPivot.rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" tick={{ fill: '#4b5563', fontSize: 12 }} />
                <YAxis
                  stroke="#6b7280"
                  tick={{ fill: '#4b5563', fontSize: 12 }}
                  tickFormatter={(v) =>
                    typeof v === 'number' ? v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : v
                  }
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [
                    `${Number(value ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L`,
                    String(name).replace('_liters', ''),
                  ]}
                />
                {monthlyPivot.years.map((y) => (
                  <Bar
                    key={`liters-${y}`}
                    dataKey={`${y}_liters`}
                    name={`${y}_liters`}
                    fill={colorByYear.get(y) ?? YEAR_COLORS[0]}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={seriesWithPct} margin={{ top: 24, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="period"
                  stroke="#6b7280"
                  tick={{ fill: '#4b5563', fontSize: 12 }}
                  tickFormatter={formatPeriodLabel}
                />
                <YAxis
                  stroke="#6b7280"
                  tick={{ fill: '#4b5563', fontSize: 12 }}
                  tickFormatter={(v) =>
                    typeof v === 'number' ? v.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : v
                  }
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                  labelFormatter={(label) => formatPeriodLabel(String(label))}
                  formatter={(value) => [
                    `${Number(value ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L`,
                    'Litres',
                  ]}
                />
                <Bar dataKey="liters" name="Litres (L)" radius={[4, 4, 0, 0]}>
                  {seriesWithPct.map((entry) => (
                    <Cell key={`liters-${entry.period}`} fill={colorForPoint(entry.period)} />
                  ))}
                  <LabelList dataKey="pctLiters" content={renderPctLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white border border-gray-200 p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold text-gray-800">{title}</div>
        {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function ChartLoader() {
  return (
    <div className="h-[320px] flex items-center justify-center text-gray-400">
      <Loader2 className="w-6 h-6 animate-spin" />
    </div>
  );
}

function ChartEmpty() {
  return (
    <div className="h-[320px] flex items-center justify-center text-sm text-gray-400">
      Aucune transaction sur la période sélectionnée.
    </div>
  );
}

/**
 * Renderer pour <LabelList content={renderPctLabel} /> — affiche la variation
 * % au-dessus de chaque barre, en rouge si augmentation (coût/litres en hausse
 * = défavorable pour la flotte) et en vert si baisse.
 */
function renderPctLabel(props: unknown): React.ReactElement {
  const p = props as { x?: number; y?: number; width?: number; value?: number | null };
  if (p.value == null || p.x == null || p.y == null || p.width == null) return <g />;
  const v = Number(p.value);
  const positive = v > 0;
  const color = v === 0 ? '#6b7280' : positive ? '#dc2626' : '#059669';
  const text = `${positive ? '+' : ''}${v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}%`;
  return (
    <text
      x={p.x + p.width / 2}
      y={p.y - 6}
      textAnchor="middle"
      fill={color}
      fontSize={11}
      fontWeight={600}
    >
      {text}
    </text>
  );
}

function PctDelta({ value }: { value: number | null }) {
  if (value == null) return <span className="text-gray-400">—</span>;
  const positive = value > 0;
  const zero = value === 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 font-medium ${
        zero ? 'text-gray-500' : positive ? 'text-rose-600' : 'text-emerald-600'
      }`}
    >
      {positive ? '▲' : zero ? '' : '▼'}
      {(positive ? '+' : '') + value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %
    </span>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
